/**
 * The offline outbox — IndexedDB, written directly rather than through a wrapper.
 *
 * Spec §10 requires App 2 to work with no connectivity: a lesson is captured in a
 * basement or on a site with no signal, and reaches the server when the phone next
 * sees a network. That means the device, not the server, is the first place a
 * record exists — so this store has to be as trustworthy as the database.
 *
 * Three decisions follow from that, and each one is a deliberate cost:
 *
 *  1. **The record id is generated here.** A form queued offline already has the
 *     UUID the server will store it under, so a photo queued beside it can name
 *     its form before either has ever been uploaded. The alternative — server-
 *     assigned ids — would need a second pass to re-point every child record, and
 *     a crash between the two passes loses the link.
 *
 *  2. **Edits are stored as field changes carrying their base value**, not as
 *     whole records. The server's three-way merge needs to know what the device
 *     started from; sending the whole record would force it to guess, and guessing
 *     is how someone's work gets silently overwritten — which spec §10 forbids.
 *
 *  3. **Nothing is removed from the outbox until the server has acknowledged it.**
 *     A failed flush leaves the queue exactly as it was. Losing a sync is an
 *     inconvenience; losing the only copy of a site observation is not.
 *
 * No third-party IndexedDB library is used. The API surface needed here is small,
 * and a dependency in the offline path is a dependency that has to be cached by
 * the service worker to be available when it matters.
 */

const DB_NAME = 'qms360-offline';
const DB_VERSION = 1;

/** Queued writes waiting for a network. */
const OUTBOX_STORE = 'outbox';
/** A local mirror of lessons, so the log and the form work with no network. */
const LESSON_STORE = 'lessons';
/** Photo bytes, held separately because they are large and uploaded one by one. */
const PHOTO_STORE = 'photos';

/** Every field a person edits (DATA-3), each carried as text. */
export type MergeableField =
  | 'title'
  | 'description'
  | 'rootCause'
  | 'correction'
  | 'correctiveAction'
  | 'repeatLocation'
  | 'disciplineId'
  | 'categorisationId'
  | 'issueCategory'
  | 'impact'
  | 'isRepeatedIssue'
  | 'repeatCount';

export interface FieldChange {
  /** What this device held when the edit began. */
  base: string | null;
  value: string;
}

export interface OutboxEntry {
  /** The lesson's id — the same one the server will use. */
  id: string;
  /** Present when this device created the record while offline. */
  create?: Record<string, unknown>;
  changes?: Partial<Record<MergeableField, FieldChange>>;
  clientUpdatedAt: string;
  /** Bumped on each failed flush, so a poisoned entry can be surfaced, not looped. */
  attempts: number;
  lastError?: string;
}

export interface QueuedPhoto {
  /** Local id; the server's photo row gets its own. */
  localId: string;
  formId: string;
  category: 'before' | 'after';
  filename: string;
  contentType: string;
  /** Base64, already resized. See `capture.ts`. */
  data: string;
  caption: string | null;
  capturedAt: string;
  attempts: number;
  lastError?: string;
}

export interface CachedLesson {
  id: string;
  [key: string]: unknown;
}

let dbPromise: Promise<IDBDatabase> | null = null;

/** True when this browser can hold an offline queue at all. */
export function isOutboxAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        db.createObjectStore(OUTBOX_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(LESSON_STORE)) {
        db.createObjectStore(LESSON_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(PHOTO_STORE)) {
        const store = db.createObjectStore(PHOTO_STORE, { keyPath: 'localId' });
        store.createIndex('formId', 'formId', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB could not be opened.'));
  });

  return dbPromise;
}

function run<T>(
  storeName: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const request = work(tx.objectStore(storeName));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed.'));
      }),
  );
}

// ---------------------------------------------------------------------------
// Outbox
// ---------------------------------------------------------------------------

export async function listOutbox(): Promise<OutboxEntry[]> {
  if (!isOutboxAvailable()) return [];
  return run<OutboxEntry[]>(OUTBOX_STORE, 'readonly', (store) => store.getAll());
}

export async function getOutboxEntry(id: string): Promise<OutboxEntry | undefined> {
  if (!isOutboxAvailable()) return undefined;
  return run<OutboxEntry | undefined>(OUTBOX_STORE, 'readonly', (store) => store.get(id));
}

/**
 * Queue a record this device created while offline.
 *
 * The payload is the create body verbatim, so the server validates it with the
 * same Zod schema an online create goes through (ADR-0010). An offline record is
 * not a lesser record.
 */
export async function queueCreate(id: string, create: Record<string, unknown>): Promise<void> {
  if (!isOutboxAvailable()) return;
  const existing = await getOutboxEntry(id);
  const entry: OutboxEntry = {
    id,
    create,
    ...(existing?.changes ? { changes: existing.changes } : {}),
    clientUpdatedAt: new Date().toISOString(),
    attempts: 0,
  };
  await run(OUTBOX_STORE, 'readwrite', (store) => store.put(entry));
}

/**
 * Queue a field edit.
 *
 * The `base` of an already-queued change is kept rather than overwritten. If the
 * user edits the same field three times offline, the server still needs to know
 * what they started from — the second and third edits are the device arguing with
 * itself, not with the server.
 */
export async function queueFieldChange(
  id: string,
  field: MergeableField,
  base: string | null,
  value: string,
): Promise<void> {
  if (!isOutboxAvailable()) return;

  const existing = await getOutboxEntry(id);
  const changes = { ...(existing?.changes ?? {}) };
  const priorBase = changes[field]?.base;

  changes[field] = {
    base: priorBase !== undefined ? priorBase : base,
    value,
  };

  const entry: OutboxEntry = {
    id,
    ...(existing?.create ? { create: existing.create } : {}),
    changes,
    clientUpdatedAt: new Date().toISOString(),
    attempts: existing?.attempts ?? 0,
  };

  await run(OUTBOX_STORE, 'readwrite', (store) => store.put(entry));
}

/** Called only once the server has confirmed the record. */
export async function clearOutboxEntry(id: string): Promise<void> {
  if (!isOutboxAvailable()) return;
  await run(OUTBOX_STORE, 'readwrite', (store) => store.delete(id));
}

/**
 * Record a failed attempt without discarding the work.
 *
 * The attempt count exists so a permanently rejected entry — a validation error
 * that will never pass, say — can be shown to the user instead of retrying
 * silently forever every time the phone finds a signal.
 */
export async function markAttemptFailed(id: string, message: string): Promise<void> {
  if (!isOutboxAvailable()) return;
  const existing = await getOutboxEntry(id);
  if (!existing) return;
  await run(OUTBOX_STORE, 'readwrite', (store) =>
    store.put({ ...existing, attempts: existing.attempts + 1, lastError: message }),
  );
}

// ---------------------------------------------------------------------------
// Local lesson mirror
// ---------------------------------------------------------------------------

export async function cacheLessons(lessons: CachedLesson[]): Promise<void> {
  if (!isOutboxAvailable()) return;
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(LESSON_STORE, 'readwrite');
    const store = tx.objectStore(LESSON_STORE);
    for (const lesson of lessons) store.put(lesson);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Caching lessons failed.'));
  });
}

export async function readCachedLessons(): Promise<CachedLesson[]> {
  if (!isOutboxAvailable()) return [];
  return run<CachedLesson[]>(LESSON_STORE, 'readonly', (store) => store.getAll());
}

export async function readCachedLesson(id: string): Promise<CachedLesson | undefined> {
  if (!isOutboxAvailable()) return undefined;
  return run<CachedLesson | undefined>(LESSON_STORE, 'readonly', (store) => store.get(id));
}

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------

export async function queuePhoto(photo: Omit<QueuedPhoto, 'attempts'>): Promise<void> {
  if (!isOutboxAvailable()) return;
  await run(PHOTO_STORE, 'readwrite', (store) => store.put({ ...photo, attempts: 0 }));
}

export async function listQueuedPhotos(formId?: string): Promise<QueuedPhoto[]> {
  if (!isOutboxAvailable()) return [];
  const all = await run<QueuedPhoto[]>(PHOTO_STORE, 'readonly', (store) => store.getAll());
  return formId ? all.filter((photo) => photo.formId === formId) : all;
}

export async function clearQueuedPhoto(localId: string): Promise<void> {
  if (!isOutboxAvailable()) return;
  await run(PHOTO_STORE, 'readwrite', (store) => store.delete(localId));
}

export async function markPhotoFailed(localId: string, message: string): Promise<void> {
  if (!isOutboxAvailable()) return;
  const existing = await run<QueuedPhoto | undefined>(PHOTO_STORE, 'readonly', (store) =>
    store.get(localId),
  );
  if (!existing) return;
  await run(PHOTO_STORE, 'readwrite', (store) =>
    store.put({ ...existing, attempts: existing.attempts + 1, lastError: message }),
  );
}

/** Test seam: drops the cached connection so a fresh database can be opened. */
export function resetOutboxConnection(): void {
  dbPromise = null;
}
